import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { Observable, Subject, Subscription } from 'rxjs';
import { map, takeUntil, withLatestFrom } from 'rxjs/operators';

import { ChangeDetectorRef, Component, effect, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '@environment';
import { AnalyticsService, AppRoutes, DynamicTranslationService, FundService as CoreFundService } from '@gs-2.0/core';
import { FundDisplay, Step } from '@gs-2.0/shared';

import { GlobalConfigApiService } from '../../../_core/api/global-config.service';
import { SignatureService } from '../../../_core/api/signature.service';
import { CONTRIBUTION_TYPE, INPUT_TYPE } from '../../../_core/constants/Contribute';
import { COUNTRY_NAMES } from '../../../_core/constants/countries';
import { EnvironmentNames } from '../../../_core/constants/environments';
import FundTypes from '../../../_core/constants/fund-types';
import PageTitles from '../../../_core/constants/page-titles';
import Urls from '../../../_core/constants/urls';
import SharedMethodsHelpers from '../../../_core/helpers/shared-methods.helpers';
import StorageHelper from '../../../_core/helpers/storage.helper';
import { Charity } from '../../../_core/models/charity.model';
import { SearchCharity } from '../../../_core/models/contributeData';
import { FeatureFlagKeys } from '../../../_core/models/FeatureFlags';
import { Fund } from '../../../_core/models/fund';
import { FeatureFlagService } from '../../../_core/services/feature-flag.service';
import { FundService } from '../../../_core/services/fund.service';
import { GlobalConfigService } from '../../../_core/services/global-config.service';
import { TitleService } from '../../../_core/services/title.service';
import { UserService } from '../../../_core/services/user.service';
import { FundNameFormatPipe } from '../../../_shared/pipes/fund-name-format.pipe';
import { DonateAsyncService } from './donate-form.async-service';
import { TaxDeductionValues } from './donate-form.const';
import { DonateFormManager } from './donate-form.manager';
import {
  AnonymityOption,
  ContributionConstData,
  ContributionDynamicData,
  CustomLinkFund,
  GetNextActionPayload,
  PaymentPayload,
  SearchCountry,
  TokenData,
} from './donate-form.model';

declare const Plaid: any;

@Component({
  selector: 'app-donate-form',
  templateUrl: './donate-form.component.html',
  styleUrls: ['./donate-form.component.scss'],
  providers: [DonateAsyncService, DonateFormManager, FundNameFormatPipe],
  standalone: false,
})
export class DonateFormComponent implements OnInit, OnDestroy {
  private _destroy$ = new Subject<void>();

  protected coreFundService = inject(CoreFundService);

  contributionTypes = CONTRIBUTION_TYPE;
  inputTypes = INPUT_TYPE;
  countryNames = COUNTRY_NAMES;
  environments = EnvironmentNames;
  anonymityOptions = AnonymityOption; // { code: false, value: 'No' }
  taxDeductionYes = TaxDeductionValues[0]; // { code: true, value: 'Yes' }
  taxDeductionNo = TaxDeductionValues[1]; // { code: false, value: 'No' }

  donateForm: FormGroup;
  constData: ContributionConstData;
  dynamicData: ContributionDynamicData;
  subscription: Subscription;
  plaidLinkHandler: any;
  additionalWarning: string;
  isRecurring = false;
  isSwiss = environment.fund.environmentName === EnvironmentNames.SWISS;

  isRecurringRoute$: Observable<string | undefined>;
  hasRecurringFlag$: Observable<boolean>;
  hasPublicRecurringFlag$: Observable<boolean>;

  publicRecurringCheckbox = new FormControl(false);
  dateFormat: string;
  numberFormatConfig = {
    decimalSymbol: '.',
    digitGroupingSymbol: ',',
    digitGrouping: '123,456,789',
  };
  isCurrencyInvalidWithDelay = false;

  /**
   * Steps configuration for the stepper component
   * mobileLabel provides shorter labels for mobile displays (≤768px)
   */
  stepperSteps: Step[] = [
    { label: 'contribute.stepper.details', number: 1 },
    { label: 'contribute.stepper.summary', number: 2 },
    {
      label: 'contribute.stepper.confirmation',
      mobileLabel: 'contribute.stepper.confirm',
      number: 3,
    },
  ];

  isDAF: boolean;
  isDAFOwner: boolean;
  allowAnonymousToOwnDAF: boolean;
  adminEmail: string;
  homeLink: string;
  displayBankErrors = false;
  plaidUnavailableInstitutions: string[] = [];
  gsUrl: string;
  redirectCustomLink: string;
  showRedirectDialog = false;
  isPayOffline = false;
  countryFieldHasNoValue = false;

  // GA4 donation-funnel tracking (public flow only — see handlePermissions()/handleNext()/checkout())
  private detailsStarted = false;
  private detailsCompleted = false;
  private summaryReached = false;
  private summaryCompleted = false;
  private isPopulatingProgrammatically = false;
  private abandonFlushed = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private titleService: TitleService,
    private userService: UserService,
    private donateAsyncService: DonateAsyncService,
    private globalConfigService: GlobalConfigService,
    public formManager: DonateFormManager,
    private cdr: ChangeDetectorRef,
    private globalConfigApiService: GlobalConfigApiService,
    private translateService: DynamicTranslationService,
    private featureFlagService: FeatureFlagService,
    private fundNameFormatPipe: FundNameFormatPipe,
    private fundService: FundService,
    private signatureService: SignatureService,
    private analyticsService: AnalyticsService
  ) {
    // Initialize properties that depend on injected services
    this.isRecurringRoute$ = this.route.queryParams.pipe(map((params) => params.isRecurring));
    this.hasRecurringFlag$ = this.featureFlagService.hasFeatureFlag(
      FeatureFlagKeys.RecurringContribution
    );
    this.hasPublicRecurringFlag$ = this.featureFlagService.hasFeatureFlag(
      FeatureFlagKeys.RecurringContributionPublic
    );
    this.isDAF = this.fundService.getCurrentFund()?.fundType === FundTypes.DAF;
    this.isDAFOwner =
      this.isDAF && this.fundService.getCurrentFund()?.contactRole?.includes('Fund Holder 1');
    this.allowAnonymousToOwnDAF =
      this.globalConfigService.getGlobalConfig()?.allowAnonymousContributionToOwnDAF ?? false;
    this.homeLink = this.route.snapshot.queryParams?.homeLink || environment.fund.homeLink;
    this.adminEmail = environment.fund.mailTo;

    this.route.queryParams.subscribe((qp) => {
      this.formManager.organizationName = qp.organizationName;
      this.formManager.donationSource = qp.utm_source;
      this.formManager.signature = qp.signature;
      this.gsUrl = qp.gsUrl;
      this.redirectCustomLink = qp.customLink;
    });

    effect(() => {
      const fund = this.coreFundService.selectedFund();
      if (fund && this.formManager.donateForm) {
        this.formManager.charity.patchValue(new Charity(fund as any), { emitEvent: false });
      }
    });
  }

  ngOnInit(): void {
    StorageHelper.removeSessionStorageItem('homeLink');
    StorageHelper.removeSessionStorageItem('gsUrl');

    if (this.formManager.signature) {
      const url = new URL(window.location.href);
      url.searchParams.delete('signature');
      const urlWithoutSignature = url.toString();
      this.formManager.data = urlWithoutSignature;

      // Validate signature
      this.signatureService
        .validateSignature(urlWithoutSignature, this.formManager.signature)
        .subscribe({
          next: (response) => {
            if (response.valid) {
              this.formManager.isSignatureValid = true;
            } else {
              this.formManager.isSignatureValid = false;
              this.formManager.donationSource = null;
              this.formManager.signature = null;
            }
          },
          error: (error) => {
            this.formManager.isSignatureValid = false;
            this.formManager.donationSource = null;
            this.formManager.signature = null;
          },
        });
    }

    this.constData = new ContributionConstData(
      this.route.snapshot.data['currentUser'] ?? this.userService.getCurrentUser(),
      this.router.url,
      this.route.snapshot
    );

    if (this.isAuthenticatedRoute) {
      this.loadCoreFundsIfNeeded();
    }

    this.isRecurringRoute$
      .pipe(withLatestFrom(this.hasRecurringFlag$))
      .subscribe(([isRecurringRoute, hasRecurringFlag]: [string | undefined, boolean]) => {
        this.isRecurring =
          ((hasRecurringFlag && isRecurringRoute) || this.isPublicRecurring) &&
          !this.constData?.isTol;
        this.setData();
        this.listenForGlobalConfig();
        this.createForm();
        this.getAllCountries();
        this.getGlobalConfiguration();

        this.publicRecurringCheckbox.valueChanges
          .pipe(takeUntil(this._destroy$))
          .subscribe((checked) => {
            checked
              ? !this.formManager.recurrenceForm && this.formManager.addRecurringControls()
              : this.formManager.removeRecurringControls();
            this.dynamicData.isPublicRecurring = this.isPublicRecurring;
          });

        this.formManager.taxDeduction.valueChanges
          .pipe(takeUntil(this._destroy$))
          .subscribe(() => this.publicRecurringCheckbox.setValue(false));
      });
  }

  private loadCoreFundsIfNeeded(): void {
    if (this.coreFundService.funds().length > 0) return;
    this.coreFundService.loadFunds().catch((error) => {
      console.error('Failed to load funds for donate form:', error);
    });
  }

  ngOnDestroy(): void {
    this.flushAbandonTracking();
    this.subscription.unsubscribe();
    this._destroy$.next();
    this._destroy$.complete();
  }

  @HostListener('window:beforeunload')
  onBeforeUnload(): void {
    this.flushAbandonTracking();
  }

  /**
   * Fires form_abandon for whichever donation-funnel step was reached but not
   * completed. Covers both tab-close/refresh (beforeunload) and in-app
   * navigation away (ngOnDestroy) — guarded so it can only fire once total,
   * whichever of the two runs first.
   */
  private flushAbandonTracking(): void {
    if (this.abandonFlushed || !this.constData?.isPublic) return;
    this.abandonFlushed = true;

    if (this.summaryReached && !this.summaryCompleted) {
      this.analyticsService.trackFormAbandon('donation', 2, 'donation_form');
    } else if (this.detailsStarted && !this.detailsCompleted) {
      this.analyticsService.trackFormAbandon('donation', 1, 'donation_form');
    }
  }

  /** Fires form_start (step 1) at most once, on the donor's first genuine edit. */
  private trackDetailsStart(): void {
    if (this.detailsStarted || this.isPopulatingProgrammatically || !this.constData?.isPublic) return;
    this.detailsStarted = true;
    this.analyticsService.trackFormStart('donation', 1, 'donation_form');
  }

  /** Fires form_complete (step 1) at most once, when Details is successfully left behind. */
  private trackDetailsComplete(): void {
    if (this.detailsCompleted || !this.constData?.isPublic) return;
    this.detailsCompleted = true;
    this.summaryReached = true;
    this.analyticsService.trackFormComplete('donation', 1, 'donation_form');
  }

  /** Fires form_complete (step 2) at most once, when the donor commits to payment/redirect. */
  private trackSummaryComplete(): void {
    if (this.summaryCompleted || !this.constData?.isPublic) return;
    this.summaryCompleted = true;
    this.analyticsService.trackFormComplete('donation', 2, 'donation_form');
  }

  getGlobalConfiguration(): void {
    // Always fetch fresh from API (same approach as activity page)
    this.globalConfigApiService.getConfiguration().subscribe({
      next: (config) => {
        if (config?.response?.dateFormat) {
          this.dateFormat = config.response.dateFormat;
        }
        if (config?.response?.numberFormat) {
          this.numberFormatConfig = {
            decimalSymbol: config.response.numberFormat.decimalSymbol?.value || '.',
            digitGroupingSymbol: config.response.numberFormat.digitGroupingSymbol?.value || ',',
            digitGrouping: config.response.numberFormat.digitGrouping || '123,456,789',
          };
          this.cdr.detectChanges();
        }
      },
    });
  }

  setData(): void {
    this.dynamicData = new ContributionDynamicData(this.constData, this.isRecurring);
    this.donateAsyncService.initContributionProps(this.constData, this.dynamicData);
    this.titleService.titleChanged(this.constData.customLink ? '' : this.constData.pageTitle);
  }

  listenForGlobalConfig(): void {
    this.subscription = this.globalConfigService.globalConfig$.subscribe((globalConfig) => {
      if (globalConfig) {
        this.constData.defaultAnonymousDonationValue =
          globalConfig.defaultAnonymousContributionValue;
      }
    });
    this.loadPlaidUnavailableInstitutions();
  }

  loadPlaidUnavailableInstitutions(): void {
    this.globalConfigApiService.getOtherConfiguration().subscribe({
      next: (otherConfig) => {
        this.plaidUnavailableInstitutions =
          otherConfig?.response?.plaidUnavailableInstitutions?.filter(
            (institution: any) => institution
          ) || [];
        this.updateDisplayBankErrors();
      },
      error: (error) => {
        this.plaidUnavailableInstitutions = [];
        this.updateDisplayBankErrors();
      },
    });
  }

  updateDisplayBankErrors(): void {
    const isBankTransferOnline =
      this.formManager?.contributionType?.value?.code === CONTRIBUTION_TYPE.BANK_TRANSFER_ONLINE;
    const hasPlaidErrors =
      this.plaidUnavailableInstitutions && this.plaidUnavailableInstitutions.length > 0;
    const isCountryAllowed = this.formManager?.shouldDisplayBankErrors() || false;
    this.displayBankErrors = isBankTransferOnline && hasPlaidErrors && isCountryAllowed;
  }

  createForm(): void {
    this.donateForm = this.formManager.initForm(
      this.constData,
      this.dynamicData,
      this.isRecurring,
      this.allowAnonymousToOwnDAF,
      this.isDAFOwner
    );

    this.formManager.contributionType.valueChanges.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.updateDisplayBankErrors();
    });

    this.donateForm.valueChanges.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.trackDetailsStart();
    });
  }

  // Set timeout to allow the form to update before checking for invalidity
  onCurrencyBlur(): void {
    setTimeout(() => {
      this.isCurrencyInvalidWithDelay =
        this.formManager.amountCurrency.invalid && this.formManager.amountCurrency.touched;
    }, 300);
  }

  getAllCountries(): void {
    this.donateAsyncService.getAllCountries().subscribe(() => {
      if (!this.constData.isPublic) this.formManager.giverInfo.patchValue(this.constData.giverInfo);
      this.handlePermissions();
      this.cdr.detectChanges();
    });
  }

  handlePermissions(): void {
    if (this.constData.token) {
      this.donateAsyncService.getRedirectInfo().subscribe((tokenData: TokenData) => {
        if (tokenData.data.recurrencePattern) {
          this.publicRecurringCheckbox.setValue(true);
          this.publicRecurringCheckbox.disable();
          !this.formManager.recurrenceForm && this.formManager.addRecurringControls();
        }
        this.isPopulatingProgrammatically = true;
        this.formManager.PrepareFormForPrefill(tokenData);
        this.isPopulatingProgrammatically = false;
        this.dynamicData.summaryData = SharedMethodsHelpers.computeSummaryData(
          this.donateForm.getRawValue(),
          this.formManager.charity.value.name
        );
        this.dynamicData.displaySummary = true;
        this.cdr.detectChanges();
      });
    }
    if (this.constData.customLink) {
      this.donateAsyncService.getCharityInfo().subscribe((charity: CustomLinkFund) => {
        this.titleService.titleChanged(
          PageTitles.GIVING_TO,
          this.fundNameFormatPipe.transform(charity.name)
        );
        this.isPopulatingProgrammatically = true;
        this.formManager.prefillAndDisableCharity(charity);
        this.isPopulatingProgrammatically = false;
        this.cdr.detectChanges();
      });
    }
    if (this.constData.isTol) {
      const southAfricaCountry = this.constData.countries.find(
        (country) => country.name === COUNTRY_NAMES.SOUTH_AFRICA
      );
      if (southAfricaCountry) {
        this.formManager.countryDonatingFrom.patchValue(southAfricaCountry, { emitEvent: false });
        if (southAfricaCountry.uuid) {
          this.donateAsyncService
            .getCountryDetails(southAfricaCountry.uuid, this.formManager.countryDonatingFrom)
            .subscribe((countryDetails: SearchCountry) => {
              this.formManager.currency.patchValue(countryDetails.currencySet[0]);
              this.cdr.detectChanges();
            });
        }
      }
    }
  }

  onSelectCountry(country: SearchCountry): void {
    // Guard: Check if country exists AND has a valid uuid
    if (!country || !country.uuid) {
      this.formManager.handleCountryChanged();
      return;
    }
    this.donateAsyncService
      .getCountryDetails(country.uuid, this.formManager.countryDonatingFrom)
      .subscribe((countryDetails: SearchCountry) => {
        this.formManager.changeCountry(countryDetails);
        this.cdr.detectChanges();
      });
    this.additionalWarning = '';
  }

  onCountrySelect(country: SearchCountry): void {
    if (country) {
      this.onSelectCountry(country);
    }
  }

  onCountryDonatingFromHasNoValue(hasNoValue: boolean): void {
    this.countryFieldHasNoValue = hasNoValue;
    if (hasNoValue) {
      this.formManager.country.patchValue(null);
      this.formManager.countryDonatingFrom.patchValue(null);
      this.formManager.handleCountryChanged();
    }
  }

  onCharitySearch(query: string): void {
    // Always search to load charities (API handles empty query to return all)
    this.searchCharity(query || '');
  }

  onCharitySelect(charity: Charity): void {
    if (charity) {
      this.onCharitySelected(charity);
    }
  }

  searchCharity(searchText: string): void {
    this.donateAsyncService
      .searchCharityByName(
        searchText,
        this.formManager.countryName,
        this.formManager.taxDeductionValue,
        this.formManager.charity
      )
      .subscribe();
  }

  onCharitySelected(charity: Charity): void {
    this.formManager.selectCharity(charity);
  }

  searchCurrency(searchText: string): void {
    const text = searchText || '';
    this.dynamicData.setCurrencySearchResults(text);
  }

  onGiverCountrySearch(event: AutoCompleteCompleteEvent): void {
    const query = event.query.toLowerCase();
    this.dynamicData.giverCountrySearchResults = this.constData.countries.filter(
      (country: { name: string }) => country.name.toLowerCase().includes(query)
    );
  }

  determineContributionStatus(submit: boolean = false): void {
    const payload = new GetNextActionPayload(
      this.donateForm.getRawValue(),
      this.formManager.charityValue,
      this.constData,
      submit
    );
    this.donateAsyncService.determineContributionStatus(payload).subscribe({
      next: () => {
        if (!submit) {
          if (this.dynamicData.willRedirect) {
            // Show dialog instead of immediately redirecting
            this.showRedirectDialog = true;
            this.dynamicData.initiatedRedirect = false; // Wait for user confirmation
          } else {
            const summaryData = SharedMethodsHelpers.computeSummaryData(
              this.donateForm.getRawValue(),
              this.getCharityName()
            );
            this.dynamicData.setSummary(summaryData);
          }
          this.trackDetailsComplete();
        }
      },
    });
  }

  confirmRedirect(): void {
    this.trackSummaryComplete();
    if (this.networkMemberRedirectUrl) {
      window.open(this.networkMemberRedirectUrl, '_self');
      this.dynamicData.initiatedRedirect = true;
      this.dynamicData.submitted = true;
      this.showRedirectDialog = false;
      return;
    }
    const targetLink = new URL(this.dynamicData.redirectTargetLink);
    const addCustomLink = this.constData.customLink || this.redirectCustomLink;

    targetLink.searchParams.set('wasLoggedIn', !this.constData.isPublic ? 'true' : 'false');
    targetLink.searchParams.set('homeLink', this.homeLink || environment.fund.homeLink);

    addCustomLink && targetLink.searchParams.set('customLink', addCustomLink);
    this.constData.utmSource && targetLink.searchParams.set('utm_source', this.constData.utmSource);
    this.constData.signature && targetLink.searchParams.set('signature', this.constData.signature);

    window.open(targetLink, '_self');
    this.dynamicData.initiatedRedirect = true;
    this.dynamicData.submitted = true;
    this.showRedirectDialog = false;
  }

  closeRedirectDialog(): void {
    this.showRedirectDialog = false;
    this.dynamicData.reset();
    window.scrollTo(0, 0);
  }

  computeSummary(): void {
    const summaryData = SharedMethodsHelpers.computeSummaryData(
      this.donateForm.getRawValue(),
      this.getCharityName()
    );
    this.dynamicData.setSummary(summaryData);
    const contributionTypeCode = this.formManager?.contributionType?.value
      ? typeof this.formManager.contributionType.value === 'string'
        ? this.formManager.contributionType.value
        : this.formManager.contributionType.value.code
      : null;
    this.dynamicData.setNextButtonText(
      contributionTypeCode,
      this.formManager.taxDeduction.value?.code,
      this.constData.isSwiss,
      this.constData.isCncf
    );
  }

  updateContributionPurpose(payload: PaymentPayload | any): PaymentPayload {
    const defaultContributionPurpose = this.translateService.instant(
      'contribute.section4.purpose.placeholder'
    );
    payload.contributionPurpose =
      !this.constData.isPublic && this.isDAFOwner && !payload.contributionPurpose
        ? defaultContributionPurpose
        : payload.contributionPurpose;
    return payload;
  }

  updateContributionType(
    payload: PaymentPayload | any,
    contributionType: CONTRIBUTION_TYPE
  ): PaymentPayload {
    return { ...payload, contributionType };
  }

  payWithStripe(): void {
    const payload = new PaymentPayload(
      this.donateForm.getRawValue(),
      this.formManager.charityValue,
      this.constData
    );
    const updatedPayload = this.updateContributionPurpose(payload);

    updatedPayload.donationSource = this.formManager.isSignatureValid
      ? this.formManager.donationSource
      : null;

    StorageHelper.saveSessionStorageItem('homeLink', this.homeLink);
    StorageHelper.saveSessionStorageItem('gsUrl', this.gsUrl);

    this.donateAsyncService
      .initiateStripe(
        this.constData.isApplicationAssessment,
        updatedPayload,
        this.isRecurring || this.isPublicRecurring
      )
      .subscribe();
  }

  resetForm(): void {
    this.formManager.resetForm();
    this.dynamicData.reset();
  }

  goHome(): void {
    if (this.constData.isPublic) {
      window.open(environment.fund.homeLink, '_self');
    } else {
      this.router.navigate([Urls.APP]);
    }
  }

  checkout(): void {
    this.trackSummaryComplete();
    // Force offline payment when configured contribution types exist and tax deduction is selected
    const hasConfiguredContributionTypes =
      this.formManager.taxDeductionValue &&
      this.formManager.networkMember?.contriConfig?.taxDeductibleContributionTypes?.length > 0;

    if (hasConfiguredContributionTypes) {
      this.payOffline();
      return;
    }

    const isNetworkMember =
      this.constData.isSwiss &&
      this.formManager.taxDeductionValue &&
      this.formManager.countryName !== COUNTRY_NAMES.SWITZERLAND &&
      this.formManager.countryName !== COUNTRY_NAMES.NETHERLANDS;

    if (isNetworkMember) {
      this.payOffline();
      return;
    }

    if (!this.formManager?.contributionType?.value) {
      return;
    }

    const contributionTypeCode =
      typeof this.formManager.contributionType.value === 'string'
        ? this.formManager.contributionType.value
        : this.formManager.contributionType.value.code;
    switch (contributionTypeCode) {
      case CONTRIBUTION_TYPE.CREDIT_CARD:
        if (
          this.constData.isSwiss &&
          this.formManager.taxDeductionValue &&
          this.formManager.countryName === COUNTRY_NAMES.NETHERLANDS
        ) {
          this.payOffline({ switchContributionType: CONTRIBUTION_TYPE.CREDIT_CARD });
        } else {
          this.payWithStripe();
        }
        break;
      case CONTRIBUTION_TYPE.SEPA_DEBIT:
        this.payWithStripe();
        break;
      case CONTRIBUTION_TYPE.BANK_TRANSFER_ONLINE:
        if (this.constData.isCncf) this.payOffline();
        else if (!this.constData.isSwiss) this.payWithPlaid();
        break;
      case CONTRIBUTION_TYPE.BANK_TRANSFER_OFFLINE:
      case CONTRIBUTION_TYPE.DONOR_ADVISED_FUND:
      case CONTRIBUTION_TYPE.PHYSICAL_CHECK:
      case CONTRIBUTION_TYPE.PUBLICLY_TRADED_SECURITIES:
      case CONTRIBUTION_TYPE.OTHER_ASSETS:
        this.payOffline();
        break;
    }
  }

  payWithPlaid(): void {
    this.dynamicData.loading = true;
    const formValue = this.donateForm.getRawValue();
    const updatedFormValue = this.updateContributionPurpose(formValue);

    updatedFormValue.donationSource = this.formManager.isSignatureValid
      ? this.formManager.donationSource
      : null;

    this.donateAsyncService
      .initiatePlaid(
        this.formManager.charityValue,
        updatedFormValue,
        this.isRecurring || this.isPublicRecurring,
        this.homeLink,
        this.redirectCustomLink,
        this.gsUrl
      )
      .subscribe((response) => {
        this.dynamicData.loading = false;
        response.onExit = (error: any) => {
          console.error('plaid exit', error);
          this.dynamicData.loading = false;
          return;
        };
        this.plaidLinkHandler = Plaid.create(response);
        this.plaidLinkHandler.open();
        this.dynamicData.loading = true;
      });
  }

  payOffline({
    switchContributionType,
  }: { switchContributionType?: CONTRIBUTION_TYPE } = {}): void {
    StorageHelper.saveTargetFundType(this.formManager.charityValue.fundType as FundTypes);

    this.isPayOffline = true;
    this.dynamicData.loading = true;
    const payload = new PaymentPayload(
      this.donateForm.getRawValue(),
      this.formManager.charityValue,
      this.constData
    );
    let updatedPayload = this.updateContributionPurpose(payload);

    if (switchContributionType) {
      updatedPayload = this.updateContributionType(payload, switchContributionType);
    }

    updatedPayload.donationSource = this.formManager.isSignatureValid
      ? this.formManager.donationSource
      : null;

    const countryDetails = this.formManager.countryDonatingFrom.value;
    const contributionTypeCode = this.formManager?.contributionType?.value
      ? typeof this.formManager.contributionType.value === 'string'
        ? this.formManager.contributionType.value
        : this.formManager.contributionType.value.code
      : null;
    this.donateAsyncService.submitOfflinePayment(
      updatedPayload,
      contributionTypeCode,
      countryDetails,
      this.formManager.taxDeductionValue,
      this.homeLink,
      this.redirectCustomLink,
      this.gsUrl,
      this.isPayOffline
    );
  }

  handleNext(): void {
    const contributionTypeCode = this.formManager?.contributionType?.value
      ? typeof this.formManager.contributionType.value === 'string'
        ? this.formManager.contributionType.value
        : this.formManager.contributionType.value.code
      : null;
    const onlineType = contributionTypeCode
      ? [CONTRIBUTION_TYPE.CREDIT_CARD, CONTRIBUTION_TYPE.BANK_TRANSFER_ONLINE].includes(
          contributionTypeCode
        )
      : false;
    const shouldDisplaySummary = !this.dynamicData.displaySummary && !this.dynamicData.willRedirect;
    const willPayFromSwiss =
      this.constData.isSwiss &&
      !this.dynamicData.willRedirect &&
      (this.dynamicData.swissContributionInfo?.stripeAvailable || !onlineType);
    const showNetworkMemberInfo =
      this.constData.isSwiss &&
      onlineType &&
      this.formManager.taxDeductionValue &&
      !this.dynamicData.willRedirect;
    const shouldGoToCheckout = willPayFromSwiss || showNetworkMemberInfo || !this.constData.isSwiss;
    switch (true) {
      case shouldDisplaySummary:
        if (this.constData.isSwiss) this.determineContributionStatus();
        else {
          this.computeSummary();
          this.trackDetailsComplete();
        }
        break;
      case shouldGoToCheckout:
        this.checkout();
        break;
      case this.dynamicData.willRedirect:
        this.determineContributionStatus(true);
        break;
    }
  }

  handleBack(): void {
    if (this.dynamicData.submitted) {
      this.goHome();
    } else {
      this.dynamicData.reset();
      window.scrollTo(0, 0);
    }
  }

  onFundChanged(fund: FundDisplay): void {
    this.coreFundService.setSelectedFund(fund.id);
    this.router.navigate([AppRoutes.FUND_DASHBOARD]);
  }

  // helpers
  getMinimumAmount(): number | null {
    const country = this.formManager.countryDonatingFrom.value;
    if (!country) return null;

    const networkMin =
      country?.networkMember?.contriConfig?.minimumContribution ??
      country?.networkMember?.config?.minimumContribution;

    if (this.formManager.taxDeductionValue && networkMin != null) {
      return networkMin;
    }

    // Return currency-specific minimum if available (set by checkMinimumAmount)
    if (this.formManager.amount?.helperData?.minAmount != null) {
      return this.formManager.amount.helperData.minAmount as number;
    }

    return null;
  }

  getCharityName(): string {
    if (this.constData.isApplicationAssessment)
      return (
        (this.formManager.charityValue as SearchCharity).name ||
        this.formManager.charityValue.fundName ||
        (this.formManager.charityValue as any).value
      );
    else
      return this.formManager.charityValue.fundName || (this.formManager.charityValue as any).value;
  }

  // getters
  get shouldShowPublicRecurring(): boolean {
    const isUSSelected =
      this.formManager.countryDonatingFrom.value?.name === COUNTRY_NAMES.UNITED_STATES &&
      !this.constData.isIgf;
    const isSwissSelected =
      this.formManager.countryDonatingFrom.value?.name === COUNTRY_NAMES.SWITZERLAND &&
      this.constData.isSwiss;
    const isBrazilSelected =
      this.formManager.countryDonatingFrom.value?.name === COUNTRY_NAMES.BRAZIL &&
      !this.constData.isFonte;

    if (this.constData.wasRedirected && this.isPublicRecurring) return true;
    if (!this.constData.isPublic) return false;
    if (isSwissSelected) return true;
    if (!!this.formManager.networkMember?.name) {
      /* NOTE: Update implementation once the following task is in progress:
        https://trustbridgeglobal.atlassian.net/browse/POR-4077 */

      return isUSSelected || isBrazilSelected ? true : !this.formManager.taxDeductionValue;
    }
    if (this.formManager.taxDeductionValue) return false;

    return true;
  }

  get isPublicRecurring(): boolean {
    if (!this.formManager?.contributionType?.value || !this.publicRecurringCheckbox) {
      return false;
    }
    const contributionTypeCode =
      typeof this.formManager.contributionType.value === 'string'
        ? this.formManager.contributionType.value
        : this.formManager.contributionType.value.code;
    return (
      this.publicRecurringCheckbox.value &&
      (contributionTypeCode === this.contributionTypes.CREDIT_CARD ||
        contributionTypeCode === this.contributionTypes.BANK_TRANSFER_ONLINE)
    );
  }

  get nextButtonDisabled(): boolean {
    // Check for charity validity error
    if (this.formManager.showCharityValidityError) {
      return true;
    }

    // For Swiss environment, explicitly check if anonymity option is selected
    if (this.isSwiss && this.displayGiverInfoStep && !this.formManager.shareDonorDetails.value) {
      return true;
    }

    // Check form validity (skip if token exists for redirect scenarios)
    return this.donateForm.invalid && !this.constData.token;
  }

  get charityFundName(): string {
    const fundName =
      this.formManager.isSwitzerlandWithCode &&
      this.formManager.shouldShowOriginalDonorRecommendation
        ? this.formManager.countryDonatingFrom.value?.networkMember?.taxDeductionFundName
        : (this.formManager.charityValue as SearchCharity)?.name ||
          (this.formManager.charityValue as Fund)?.fundName;
    const fundNumber = this.formManager.isSwitzerlandWithCode
      ? ''
      : this.formManager.charityValue?.fundNumber
        ? `(#${this.formManager.charityValue.fundNumber})`
        : '';
    return `${fundName} ${fundNumber}`;
  }

  get charityNameOnly(): string {
    if (
      this.formManager.isSwitzerlandWithCode &&
      this.formManager.shouldShowOriginalDonorRecommendation
    ) {
      return this.formManager.countryDonatingFrom.value?.networkMember?.taxDeductionFundName || '';
    }

    const name =
      (this.formManager.charityValue as SearchCharity)?.name ||
      (this.formManager.charityValue as Fund)?.fundName ||
      '';
    return this.fundNameFormatPipe.transform(name.trim());
  }

  get redirectLogoUrl(): string {
    switch (this.dynamicData.redirectTarget) {
      case this.environments.IGF:
        return 'assets/logos/igf.png';
      case this.environments.CNCF:
        return 'assets/logos/cncf.png';
      case this.environments.SINNGEBER:
        return 'assets/logos/sinngeber-white-bg.jpg';
      case this.environments.FONTE:
        return 'assets/logos/fonte_logo_white.png';
      default:
        return '';
    }
  }

  get redirectOrganizationName(): string {
    const networkMember = this.formManager.countryDonatingFrom.value?.networkMember;
    return networkMember?.name || '';
  }

  get allowsRecurring(): boolean {
    this.dynamicData.publicRecurringWarningMessage = '';

    if (
      this.formManager.countryDonatingFrom.value?.name === COUNTRY_NAMES.UNITED_STATES &&
      !this.constData.isIgf
    ) {
      if (this.constData.isPublic) {
        /* NOTE: Update implementation once the following task is in progress:
           https://trustbridgeglobal.atlassian.net/browse/POR-4077 */

        // this.dynamicData.publicRecurringWarningMessage = 'FORM_VALIDATORS_RECURRING_WARNING_US';
        return true;
      } else {
        this.dynamicData.warningMessage = 'FORM_VALIDATORS_RECURRING_WARNING_US';
        return false;
      }
    }

    if (
      this.formManager.countryDonatingFrom.value?.name === COUNTRY_NAMES.CANADA &&
      this.formManager.taxDeduction.value?.code &&
      !this.constData.isCncf
    ) {
      if (this.constData.isPublic) {
        this.dynamicData.publicRecurringWarningMessage = 'FORM_VALIDATORS_RECURRING_WARNING_CA';
        return true;
      } else {
        this.additionalWarning = 'FORM_VALIDATORS_RECURRING_WARNING_CA';
        return false;
      }
    }

    return true;
  }

  get displayContributionTypeStep(): boolean {
    if (this.isRecurring && !this.allowsRecurring) return false;
    if (this.donateForm.disabled && this.constData.isPublic) return true;
    return (
      this.formManager.countryAndCharityInfo.valid &&
      !this.formManager.countryDonatingFrom.loading &&
      !this.formManager.charity.loading &&
      !this.dynamicData.warningMessage
    );
  }

  get isNetherlandsTaxDeduction(): boolean {
    return (
      this.formManager.countryDonatingFrom.value?.name === COUNTRY_NAMES.NETHERLANDS &&
      this.formManager.taxDeductionValue
    );
  }

  get displayFinancialStep(): boolean {
    if (this.dynamicData.publicRecurringWarningMessage && this.isPublicRecurring) return false;
    if (this.isSinngeberGivingFund) return false;
    if (this.donateForm.disabled) return true;
    return this.displayContributionTypeStep && this.formManager.contributionTypeInfo.valid;
  }

  get displayGiverInfoStep(): boolean {
    if (this.dynamicData.publicRecurringWarningMessage && this.isPublicRecurring) return false;
    if (this.donateForm.disabled) return true;
    return (
      this.displayFinancialStep &&
      this.formManager.financialInfo.valid &&
      this.formManager.contributionTypeInfo.valid &&
      !this.formManager.amount.loading &&
      !this.dynamicData.warningMessage
    );
  }

  get displayMinAmountError(): boolean {
    return (
      !this.formManager.amount.loading &&
      this.formManager.amount.helperData.minAmount &&
      this.formManager.currency.value &&
      this.formManager.amount.value
    );
  }

  get displayContributionSummary(): boolean {
    const showSwissSummary =
      this.constData.isSwiss &&
      !this.dynamicData.submitted &&
      this.dynamicData.swissContributionInfo &&
      !this.dynamicData.willRedirect;
    return showSwissSummary || (!this.constData.isSwiss && !this.dynamicData.submitted);
  }

  get displayFooterSection(): boolean {
    const isCharitySelected: boolean = !this.constData.isPublic || !!this.formManager.charity.value;
    if (this.isSinngeberGivingFund) return false;
    if (this.constData.customLink && !this.dynamicData.canDonate) return false;
    return isCharitySelected && !this.dynamicData.submitted && !this.showRedirectDialog;
  }

  get isSinngeberGivingFund(): boolean {
    if (!this.formManager?.contributionType?.value) {
      return false;
    }
    const contributionTypeCode =
      typeof this.formManager.contributionType.value === 'string'
        ? this.formManager.contributionType.value
        : this.formManager.contributionType.value.code;
    return (
      this.constData.isSinngeber &&
      contributionTypeCode === this.contributionTypes.DONOR_ADVISED_FUND
    );
  }

  get taxDeductibleMessage(): string | null {
    const customMessage =
      this.formManager.countryDonatingFrom.value?.networkMember?.contriConfig
        ?.taxDeductibleRequirementsMessage;

    // If null or undefined, use fallback message
    if (customMessage === null || customMessage === undefined) {
      return this.dynamicData.networkMemberInfo;
    }

    // If empty string, return null to hide the info-box
    if (customMessage === '') {
      return null;
    }

    // Non-empty string: return the custom message
    return customMessage;
  }

  get usStateOptions(): { name: string }[] {
    return this.constData?.usStates?.map((state) => ({ name: state })) ?? [];
  }

  /**
   * Helper method to check if a form control has the required validator
   * @param control The form control to check
   * @returns true if the control is required, false otherwise
   */
  isRequired(control: FormControl): boolean {
    if (!control) return false;
    return control.hasValidator(Validators.required);
  }

  /**
   * Helper method to get the contribution type code
   * Handles both string values (from p-select) and object values (legacy)
   * @returns The contribution type code string
   */
  getContributionTypeCode(): string | null {
    if (!this.formManager?.contributionType?.value) {
      return null;
    }
    const value = this.formManager.contributionType.value;
    return typeof value === 'string' ? value : value.code;
  }

  /**
   * Get the current step index for the stepper component (0-based)
   * Step 0 (Details): Form is being filled out
   * Step 1 (Summary): Summary is displayed before final submission
   * Step 2 (Confirmation): Form has been submitted or will redirect
   */
  get currentStepIndex(): number {
    if (this.dynamicData.submitted || this.dynamicData.initiatedRedirect) {
      return 2; // Confirmation step
    }
    if (
      this.dynamicData.displaySummary ||
      (this.dynamicData.willRedirect && !this.showRedirectDialog)
    ) {
      return 1; // Summary step
    }
    return 0; // Details step (default)
  }

  get networkMemberRedirectUrl(): string | null {
    return (
      this.formManager.countryDonatingFrom?.value?.networkMember?.contriConfig?.redirectUrl || null
    );
  }

  get isAuthenticatedRoute(): boolean {
    return this.router.url.startsWith('/fund/');
  }
}
